"use client";

import React, { FC, Fragment, useEffect, useMemo, useState } from "react";
import { discountErrorMessage } from "@/app/lib/discountErrors";
import { Dialog, Transition } from "@headlessui/react";
import Link from "next/link";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "@/app/lib/hooks";
import { deleteCart, getCart } from "@/app/lib/reducers/cartReducer";
import {
  checkDiscountCode,
  resetDiscount,
} from "@/app/lib/reducers/ordersReducer";
import { useCdnUrl } from "@/hooks/useCdnUrl";
import { fetchExchangeRate } from "@/utils/currencyUtils";
import { lineTotalFor } from "@/utils/pricingUtils";
import { cartLineCityId, useAutoAccountDiscount } from "@/app/lib/useAccountDiscounts";
import ResaleHint from "@/components/ResaleHint";

export interface CartModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Cart modal — replaces the legacy CartDropdown tooltip with a centered
 * full-page modal matching the 2026 redesign. Reads the cart from Redux
 * (`state.cart.items`, populated by getCart), renders each booking with
 * image + city + name + datetime + travellers + language + price + a
 * "Retirer" link, and a footer with promo code, totals and CTAs.
 *
 * Promo code uses the existing `checkDiscountCode` action which already
 * powers /checkout — no new endpoint needed.
 */
const CartModal: FC<CartModalProps> = ({ open, onClose }) => {
  const { t } = useTranslation("guydeez");
  const dispatch = useAppDispatch();
  const toCdnUrl = useCdnUrl();

  const items = useAppSelector((s) => s.cart.items) || [];
  const selectedLanguage = useAppSelector(
    (s) => s.language.selectedLanguage || "fr_FR"
  );
  // Prices in the cart are stored in EUR; the modal must display them in the
  // user's selected currency (the URL/header currency), like the checkout page.
  const selectedCurrency = (
    useAppSelector((s: any) => s.currencies?.selectedCurrency) || "EUR"
  ).toUpperCase();
  const connectedUser = useAppSelector((s) => s.auth.user);
  const discountAmount = useAppSelector(
    (s: any) => s.orders?.discountAmount ?? null
  );
  const discountType = useAppSelector(
    (s: any) => s.orders?.discountType ?? null
  );
  // Motif du refus (code invalide, non connecte, ville…) : voir lib/discountErrors.
  const discountCheckError = useAppSelector((s: any) => s.orders?.error);
  const discountValid = useAppSelector(
    (s: any) => s.orders?.discountValid ?? null
  );
  const discountAllowedCities: number[] | null = useAppSelector(
    (s: any) => s.orders?.discountAllowedCityIds ?? null
  );

  const [promoInput, setPromoInput] = useState("");
  const [promoChecking, setPromoChecking] = useState(false);
  const [promoError, setPromoError] = useState<string | null>(null);
  // EUR -> selected-currency rate (1 for EUR). Fetched once per currency.
  const [exchangeRate, setExchangeRate] = useState(1);

  // Refresh cart whenever the modal opens for a logged-in user — covers the
  // case where another tab added an item while this one was idle.
  useEffect(() => {
    if (!open) return;
    if (connectedUser?.uuid && selectedLanguage) {
      dispatch(getCart(selectedLanguage));
    }
  }, [open, connectedUser?.uuid, selectedLanguage, dispatch]);

  // Keep the EUR -> selected-currency rate in sync with the chosen currency.
  useEffect(() => {
    let active = true;
    if (selectedCurrency === "EUR") {
      setExchangeRate(1);
      return;
    }
    fetchExchangeRate("EUR", selectedCurrency)
      .then((r) => {
        if (active) setExchangeRate(typeof r === "number" && isFinite(r) ? r : 1);
      })
      .catch(() => {
        if (active) setExchangeRate(1);
      });
    return () => {
      active = false;
    };
  }, [selectedCurrency]);

  // Line price = unit pricing × traveller count (same formula as the
  // /checkout page). `it.price` alone is empty on freshly-hydrated carts.
  // lineTotalFor knows whether the row is per-traveller or a flat group
  // total, so this stays correct for both. Falls back to the stored
  // it.price only when the row itself is missing (freshly-hydrated carts).
  const lineEUR = (it: any) => {
    const adults = it.travellers?.adults ?? 0;
    const children = (it.travellers as any)?.children ?? 0;
    const line = lineTotalFor(it.pricing, adults + children);
    if (line != null) return line;
    const stored = Number(it.price ?? 0);
    return Number.isFinite(stored) ? stored : 0;
  };
  const subtotal = useMemo(
    () => items.reduce((acc, it) => acc + lineEUR(it), 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items]
  );
  // Un code limite a certaines villes ne porte que sur leurs lignes — meme regle que la
  // page de paiement et le serveur. Le tiroir l'appliquait au panier entier.
  const eligibleSubtotal = useMemo(
    () =>
      Array.isArray(discountAllowedCities) && discountAllowedCities.length > 0
        ? items.reduce((acc, it) => {
            const cityId = cartLineCityId(it);
            return cityId !== null && discountAllowedCities.includes(Number(cityId)) ? acc + lineEUR(it) : acc;
          }, 0)
        : subtotal,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, discountAllowedCities, subtotal]
  );

  // Remise du compte pro, appliquee sans saisie (GT-261007-HVGQ).
  const accountDiscount = useAutoAccountDiscount(
    items.map((it) => ({ cityId: cartLineCityId(it), amount: lineEUR(it) }))
  );

  // Discount calculation respects the `type` returned by the backend:
  //   - "percentage" → discountAmount is a 0-100 ratio applied to subtotal
  //   - anything else (or null type) → discountAmount is a fixed € value
  // Discount can never exceed the subtotal (clamped via Math.min).
  const rawDiscount = Number(discountAmount) || 0;
  const discount = useMemo(() => {
    if (discountValid !== true || rawDiscount <= 0) return 0;
    const amount =
      discountType === "percentage"
        ? eligibleSubtotal * (Math.min(100, rawDiscount) / 100)
        : rawDiscount;
    return Math.min(eligibleSubtotal, Math.max(0, amount));
  }, [discountValid, discountType, rawDiscount, eligibleSubtotal]);
  const total = Math.max(0, subtotal - discount);

  // Format an EUR amount in the selected currency with proper symbol + 2
  // decimals. Replaces the old `Math.round(n) + " €"` which (a) hid the cents
  // so the lines looked inconsistent (50 − 49 = 0 when the real values were
  // 49.50 − 49.01 = 0.49) and (b) always showed € regardless of the chosen
  // currency. EUR amounts are converted via the live rate before formatting.
  const currencyFmt = useMemo(() => {
    const locale = (selectedLanguage || "fr_FR").replace("_", "-");
    try {
      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency: selectedCurrency,
      });
    } catch {
      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency: "EUR",
      });
    }
  }, [selectedLanguage, selectedCurrency]);
  const formatPrice = (n: number | undefined | null) => {
    const num = Number(n);
    if (!isFinite(num)) return "—";
    return currencyFmt.format(num * exchangeRate);
  };

  // When the cart composition changes, any previously-applied discount may
  // no longer match (cities/categories filter, minimum amount, etc.) — so
  // we silently reset it and surface a notice asking the user to re-enter
  // the code to keep the total coherent.
  const [discountResetNotice, setDiscountResetNotice] = useState(false);

  const handleRemove = (uuid?: string | null) => {
    if (!uuid) return;
    const hadDiscount = discountValid === true && discount > 0;
    dispatch(deleteCart(uuid));
    if (hadDiscount) {
      dispatch(resetDiscount());
      setAppliedCode(null);
      setDidAttempt(false);
      // La remise du compte se re-applique toute seule au nouveau panier.
      setDiscountResetNotice(!accountDiscount);
    }
  };

  // Local flag so we only surface the "invalid code" error after the user
  // actually clicked Appliquer with a non-empty input. Without it, clearing
  // an applied discount (which the reducer marks as `discountValid: false`)
  // would flash the error message even though no code was just attempted.
  const [didAttempt, setDidAttempt] = useState(false);
  // Snapshot of the code at the moment it was successfully applied. The
  // reducer only stores the uuid/value/type, not the human-readable code
  // the user typed — so we keep our own copy for the totals line label.
  const [appliedCode, setAppliedCode] = useState<string | null>(null);

  const handlePromoApply = async () => {
    const code = promoInput.trim();
    if (!code) return;
    setPromoChecking(true);
    setPromoError(null);
    setDidAttempt(true);
    setDiscountResetNotice(false);
    try {
      const firstCity = items[0]?.tour?.city?.id ?? null;
      await dispatch(checkDiscountCode(code, firstCity) as any);
      setAppliedCode(code.toUpperCase());
    } finally {
      setPromoChecking(false);
    }
  };

  const handlePromoClear = () => {
    setPromoInput("");
    setPromoError(null);
    setDidAttempt(false);
    setAppliedCode(null);
    dispatch(resetDiscount());
  };

  const discountApplied = discountValid === true && discount > 0;
  // Pretty discount label: "−100%" for percentage, "−25 €" otherwise.
  const discountLabel =
    discountType === "percentage"
      ? `−${Math.min(100, rawDiscount).toLocaleString("fr-FR")}%`
      : `−${formatPrice(rawDiscount)}`;

  return (
    <Transition show={open} as={Fragment}>
      <Dialog as="div" className="relative z-[200]" onClose={onClose}>
        {/* Backdrop */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
        </Transition.Child>

        {/* Modal */}
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-start justify-center p-4 pt-[8vh]">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 translate-y-2 scale-95"
              enterTo="opacity-100 translate-y-0 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0 scale-100"
              leaveTo="opacity-0 translate-y-2 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl rounded-2xl bg-white shadow-2xl overflow-hidden">
                {/* Header */}
                <div className="flex items-start justify-between px-6 pt-6 pb-4">
                  <div>
                    <h2 className="text-xl font-bold text-[#5B1F3A]">
                      {t("cart_modal_title", { defaultValue: "Votre panier" })}
                    </h2>
                    <p className="text-xs text-neutral-500 mt-0.5">
                      {items.length > 0
                        ? t("cart_modal_subtitle", {
                            count: items.length,
                            defaultValue: `${items.length} ${items.length === 1 ? "expérience" : "expériences"}`,
                          })
                        : t("cart_modal_empty_subtitle", {
                            defaultValue: "Aucune expérience pour le moment",
                          })}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={onClose}
                    aria-label="Close"
                    className="w-8 h-8 rounded-full bg-neutral-100 hover:bg-neutral-200 flex items-center justify-center text-neutral-500"
                  >
                    ✕
                  </button>
                </div>

                {/* Items */}
                {items.length === 0 ? (
                  <div className="px-6 py-12 text-center">
                    <p className="text-sm text-neutral-500">
                      {t("cart_modal_empty", {
                        defaultValue:
                          "Votre panier est vide. Trouvez une expérience pour commencer.",
                      })}
                    </p>
                  </div>
                ) : (
                  <>
                    <ul className="px-6 max-h-[40vh] overflow-y-auto flex flex-col gap-3">
                      {items.map((it, i) => {
                        const tour = it.tour || {};
                        const city = tour.city?.name || "";
                        const img =
                          tour.entry?.galleries?.[0]?.path
                            ? toCdnUrl(tour.entry.galleries[0].path)
                            : null;
                        const date = it.datetime?.date;
                        const time = formatTime(it.datetime?.time);
                        const adults = it.travellers?.adults ?? 0;
                        const children = (it.travellers as any)?.children ?? 0;
                        const people = adults + children;
                        const rawDur =
                          it.selectedDuration?.duration || it.duration;
                        const duration = rawDur ? `${rawDur}h` : "";
                        // The cart row stores the field as `language` (the
                        // legacy `guideLanguage` name only exists on the
                        // addToCart input). BCP-47-ish ("fr_FR"). Second
                        // chunk = ISO-2 country for the flag (matches
                        // flagcdn.com's lowercase keys).
                        const rawLang =
                          (it as any).language || it.guideLanguage || "";
                        const langParts = rawLang.split("_");
                        const langCode = langParts[0]?.toLowerCase();
                        const flagCode = langParts[1]?.toLowerCase();
                        // Localized display name ("Français", "English",
                        // "日本語"...) via Intl.DisplayNames. Falls back to
                        // the uppercased code if the API isn't available
                        // or the code is unknown.
                        const lang = localizeLanguage(langCode, selectedLanguage);
                        // Per-item line price — same helper as the subtotal above
                        // and as /checkout, so a "Group" row is never multiplied by
                        // the traveller count. `it.price` on its own is empty on
                        // freshly-loaded carts, hence the fallback.
                        const linePrice =
                          lineTotalFor(it.pricing, people) ?? Number(it.price ?? 0);
                        return (
                          <li
                            key={it.uuid || i}
                            className="flex items-start gap-4 py-2"
                          >
                            <div
                              className="w-16 h-16 rounded-lg flex-shrink-0 bg-neutral-100 bg-cover bg-center"
                              style={
                                img
                                  ? { backgroundImage: `url("${encodeURI(img)}")` }
                                  : undefined
                              }
                            />
                            <div className="flex-1 min-w-0">
                              {city && (
                                <div className="text-[10px] font-bold uppercase tracking-widest text-[#C13B5C]">
                                  {city}
                                </div>
                              )}
                              <div className="text-sm font-bold text-[#5B1F3A] truncate">
                                {tour.name || it.name || ""}
                              </div>
                              <div className="text-xs text-neutral-500 mt-1 flex flex-wrap gap-x-2">
                                {date && <span>{formatDate(date)}</span>}
                                {time && <span>· {time}</span>}
                                {people > 0 && (
                                  <span>
                                    · {people}{" "}
                                    {t("cart_modal_people_short", {
                                      count: people,
                                      defaultValue: "pers.",
                                    })}
                                  </span>
                                )}
                                {duration && <span>· {duration}</span>}
                                {lang && (
                                  <span className="inline-flex items-center gap-1.5">
                                    ·{" "}
                                    {flagCode && flagCode.length === 2 && (
                                      <img
                                        src={`https://flagcdn.com/${flagCode}.svg`}
                                        alt={lang}
                                        width={16}
                                        height={12}
                                        loading="lazy"
                                        style={{
                                          width: 16,
                                          height: 12,
                                          objectFit: "cover",
                                          borderRadius: 1.5,
                                          boxShadow: "0 0 0 1px rgba(0,0,0,0.08)",
                                          flexShrink: 0,
                                        }}
                                      />
                                    )}
                                    {lang}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="text-right flex flex-col items-end gap-1 flex-shrink-0">
                              <div className="text-base font-bold text-[#5B1F3A]">
                                {formatPrice(linePrice)}
                              </div>
                              <button
                                type="button"
                                onClick={() => handleRemove(it.uuid)}
                                className="text-xs text-neutral-500 hover:text-[#C13B5C] underline"
                              >
                                {t("cart_modal_remove", {
                                  defaultValue: "Retirer",
                                })}
                              </button>
                            </div>
                          </li>
                        );
                      })}
                    </ul>

                    {/* Promo. Two states:
                          - not applied → input + Appliquer
                          - applied     → cream pill with the code + ✕   */}
                    <div className="px-6 mt-4">
                      {discountApplied ? (
                        <div className="flex items-center justify-between gap-2 px-4 py-2 bg-[#E8F8EE] border border-[#4FBF6E] rounded-lg">
                          <span className="text-sm font-semibold text-[#2E8C4D] inline-flex items-center gap-2 flex-wrap">
                            <CheckIcon />
                            {appliedCode || accountDiscount ? (
                              <>
                                <span className="text-[#5B1F3A]">{appliedCode || accountDiscount?.name}</span>
                                <span>—</span>
                              </>
                            ) : null}
                            {accountDiscount && !appliedCode
                              ? t("account_discount_applied_short", {
                                  defaultValue: "Remise de votre compte, appliquée automatiquement",
                                })
                              : t("cart_modal_promo_applied_short", {
                                  defaultValue: "Code appliqué",
                                })}
                            <span className="text-neutral-500 font-normal">
                              · {discountLabel}
                            </span>
                          </span>
                          <button
                            type="button"
                            onClick={handlePromoClear}
                            className="text-xs text-neutral-500 hover:text-[#C13B5C] underline"
                          >
                            {t("cart_modal_remove", { defaultValue: "Retirer" })}
                          </button>
                        </div>
                      ) : (
                        <form
                          className="flex gap-2"
                          onSubmit={(e) => {
                            e.preventDefault();
                            handlePromoApply();
                          }}
                        >
                          <input
                            type="text"
                            value={promoInput}
                            onChange={(e) => setPromoInput(e.target.value)}
                            placeholder={t("cart_modal_promo_placeholder", {
                              defaultValue: "Code promo",
                            })}
                            className="flex-1 px-4 py-2 border border-neutral-200 rounded-lg text-sm focus:outline-none focus:ring-0 focus:border-[#4FBF6E]"
                          />
                          <button
                            type="submit"
                            disabled={promoChecking || !promoInput.trim()}
                            className="px-4 py-2 rounded-lg border border-[#5B1F3A] text-[#5B1F3A] text-sm font-semibold hover:bg-[#5B1F3A] hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {promoChecking
                              ? "…"
                              : t("cart_modal_promo_apply", {
                                  defaultValue: "Appliquer",
                                })}
                          </button>
                        </form>
                      )}
                    </div>
                    {didAttempt && discountValid === false && !discountApplied && (
                      <p className="px-6 mt-2 text-xs text-[#C13B5C]">
                        {discountErrorMessage(discountCheckError, t as any)}
                      </p>
                    )}
                    {discountResetNotice && !discountApplied && (
                      <p className="px-6 mt-2 text-xs text-[#C13B5C] font-medium">
                        ⚠{" "}
                        {t("cart_modal_promo_reset_notice", {
                          defaultValue:
                            "Le code promo a été retiré suite à la modification du panier. Resaisissez-le pour mettre à jour le total.",
                        })}
                      </p>
                    )}

                    {/* Totals */}
                    <div className="mx-6 mt-4 p-4 bg-[#FDF4E8] rounded-lg space-y-2 text-sm">
                      <div className="flex justify-between text-neutral-700">
                        <span>
                          {t("cart_modal_subtotal", {
                            defaultValue: "Sous-total",
                          })}
                        </span>
                        <span className="font-semibold">{formatPrice(subtotal)}</span>
                      </div>
                      {discount > 0 && (
                        <div className="flex justify-between text-[#C13B5C]">
                          <span className="font-semibold">
                            {appliedCode || accountDiscount
                              ? `${t("cart_modal_promo_code_word", { defaultValue: "Code" })} ${appliedCode || accountDiscount?.name}`
                              : t("cart_modal_promo_applied", { defaultValue: "Code promo" })}{" "}
                            <span className="text-neutral-500 font-normal">
                              ({discountLabel})
                            </span>
                          </span>
                          <span className="font-semibold">
                            −{formatPrice(discount)}
                          </span>
                        </div>
                      )}
                      <div className="border-t border-neutral-200/70 pt-2 flex justify-between items-end">
                        <span className="font-bold text-[#5B1F3A] text-base">
                          {t("cart_modal_total", { defaultValue: "Total" })}
                        </span>
                        <div className="text-right">
                          <div className="font-bold text-[#5B1F3A] text-lg leading-none">
                            {formatPrice(total)}
                          </div>
                          <div className="text-[10px] text-neutral-500 mt-1">
                            {t("cart_modal_cancel_note", {
                              defaultValue: "Annulation gratuite 24h avant",
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* Prix de revente conseille, comptes pro seulement (GT-261007-HVGQ) */}
                    <div className="mx-6 mt-2">
                      <ResaleHint amountEUR={total} format={formatPrice} />
                    </div>

                    {/* CTAs */}
                    <div className="px-6 pt-5 pb-4">
                      <Link
                        href={"/checkout" as any}
                        onClick={onClose}
                        className="block w-full text-center py-3 rounded-lg bg-[#C13B5C] text-white font-semibold hover:bg-[#a3304d]"
                      >
                        {t("cart_modal_checkout", {
                          defaultValue: "Procéder à la commande →",
                        })}
                      </Link>
                      <button
                        type="button"
                        onClick={onClose}
                        className="block w-full mt-2 py-2 text-center text-sm text-neutral-600 hover:text-[#5B1F3A]"
                      >
                        {t("cart_modal_continue", {
                          defaultValue: "Continuer mes recherches",
                        })}
                      </button>
                    </div>

                    {/* Trust badges */}
                    <div className="px-6 pb-6 flex items-center justify-center gap-6 text-[12px] text-neutral-600">
                      <span>
                        🔒{" "}
                        {t("cart_modal_trust_secure_payment", {
                          defaultValue: "Paiement sécurisé",
                        })}
                      </span>
                      <span>
                        ✓{" "}
                        {t("cart_modal_trust_free_cancel", {
                          defaultValue: "Annulation gratuite",
                        })}
                      </span>
                      <span>
                        ⚡{" "}
                        {t("cart_modal_trust_instant_confirm", {
                          defaultValue: "Confirmation immédiate",
                        })}
                      </span>
                    </div>
                  </>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

// Convert a 2-letter language code ("fr", "en") into the user-facing
// language name ("Français", "English") localized to the current UI lang.
// Falls back to the uppercased code if Intl.DisplayNames is missing
// (older browsers) or the code is unknown.
const localizeLanguage = (code: string | undefined, uiLang: string): string => {
  if (!code) return "";
  const upper = code.toUpperCase();
  if (typeof Intl === "undefined" || typeof Intl.DisplayNames !== "function") {
    return upper;
  }
  try {
    const dn = new Intl.DisplayNames([uiLang.replace("_", "-")], {
      type: "language",
    });
    const name = dn.of(code);
    if (!name) return upper;
    // Intl returns lowercased first letter for some locales — capitalize
    // so the chip reads "Français" not "français".
    return name.charAt(0).toUpperCase() + name.slice(1);
  } catch {
    return upper;
  }
};

const CheckIcon = () => (
  <svg
    width={14}
    height={14}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M5 13l4 4L19 7" />
  </svg>
);

const formatDate = (iso: string) => {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
};

// French-style time: "10:30" → "10h30", "9:5" → "9h05". Pass-through if
// the input doesn't look like HH:MM.
const formatTime = (t?: string | null) => {
  if (!t) return "";
  const m = /^(\d{1,2}):(\d{1,2})$/.exec(t.trim());
  if (!m) return t;
  return `${m[1]}h${m[2].padStart(2, "0")}`;
};

export default CartModal;
